<template>
  <div v-if="hasAccess" class="meetings-page">
    <page-content class="meetings-page__content">
      <section class="meetings-panel">
        <header class="meetings-toolbar">
          <label class="meetings-search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
              v-model.trim="search"
              type="search"
              placeholder="Rechercher une réunion"
              aria-label="Rechercher une réunion par titre, fichier ou statut"
            >
          </label>
          <div class="meetings-toolbar__actions">
            <router-link :to="{ name: 'app-meetings-context' }" class="meetings-outline">
              <i class="fas fa-book-open" aria-hidden="true"></i> Dossier de suivi
            </router-link>
            <router-link :to="{ name: 'app-meetings-new' }" class="meetings-action">
              <i class="fas fa-plus-circle" aria-hidden="true"></i> Déposer une réunion
            </router-link>
          </div>
        </header>

        <div v-if="loading" class="meetings-state">
          <i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Chargement des réunions…
        </div>
        <div v-else-if="loadError" class="meetings-state meetings-state--error">
          <span>{{ loadError }}</span>
          <button type="button" @click="loadMeetings()">Réessayer</button>
        </div>
        <div v-else-if="!meetings.length" class="meetings-state meetings-empty">
          <p class="meetings-empty__title">Aucune réunion déposée pour ce domaine.</p>
          <p>
            Déposez l’enregistrement audio ou vos notes de séance : vous obtiendrez un résumé,
            des propositions d’actualités et les points de suivi de la réunion.
          </p>
        </div>
        <div v-else class="meetings-table-scroll">
          <table class="meetings-table">
            <thead>
              <tr>
                <th class="meetings-column--title">Réunion</th>
                <th class="meetings-column--date">Date de réunion</th>
                <th class="meetings-column--type">Entrée</th>
                <th class="meetings-column--status">Statut</th>
                <th class="meetings-column--created">Déposée le</th>
                <th class="meetings-column--delete"><span class="sr-only">Supprimer</span></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="meeting in displayedMeetings"
                :key="meeting.uuid"
                class="meetings-row"
                tabindex="0"
                @click="open(meeting)"
                @keydown.enter.self="open(meeting)"
              >
                <td class="meetings-column--title">
                  <span class="meetings-title" :title="meeting.title">{{ meeting.title || 'Réunion sans titre' }}</span>
                  <span v-if="meeting.audio_file && meeting.audio_file.name" class="meetings-detail">
                    {{ meeting.audio_file.name }}<template v-if="meeting.audio_file.size"> · {{ formatFileSize(meeting.audio_file.size) }}</template>
                  </span>
                  <span v-else-if="meeting.error && meeting.status === 'failed'" class="meetings-detail meetings-detail--error">
                    {{ meeting.error }}
                  </span>
                </td>
                <td class="meetings-column--date">{{ formatMeetingDate(meeting.meeting_date) || '—' }}</td>
                <td class="meetings-column--type">
                  <span class="meetings-type" :class="`meetings-type--${meeting.input_type}`">
                    {{ meeting.input_type === 'audio' ? 'Audio' : 'Notes' }}
                  </span>
                </td>
                <td class="meetings-column--status"><meeting-status-pill :status="meeting.status" /></td>
                <td class="meetings-column--created">{{ formatDateTime(meeting.created_at) || '—' }}</td>
                <td class="meetings-column--delete">
                  <button
                    type="button"
                    class="meetings-delete"
                    title="Supprimer la réunion"
                    :disabled="deleting === meeting.uuid"
                    @click.stop="confirmDelete(meeting)"
                  >
                    <i :class="deleting === meeting.uuid ? 'fas fa-spinner fa-spin' : 'far fa-trash-alt'" aria-hidden="true"></i>
                    <span class="sr-only">Supprimer {{ meeting.title }}</span>
                  </button>
                </td>
              </tr>
              <tr v-if="!displayedMeetings.length">
                <td colspan="6" class="meetings-state">Aucune réunion ne correspond à votre recherche.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <footer v-if="!loading && !loadError && meetings.length" class="meetings-footer">
          <span v-if="processingCount" class="meetings-footer__processing">
            <i class="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
            {{ processingCount > 1 ? `${processingCount} réunions en cours de traitement` : 'Une réunion en cours de traitement' }} · actualisation automatique
          </span>
          <span v-if="reviewCount" class="meetings-footer__review">
            <i class="fas fa-user-check" aria-hidden="true"></i>
            {{ reviewCount > 1 ? `${reviewCount} réunions attendent votre relecture` : 'Une réunion attend votre relecture' }}
          </span>
          <span class="meetings-footer__count">{{ countLabel }}</span>
        </footer>
      </section>
    </page-content>
  </div>

  <div v-else class="meetings-no-access">
    <p>Vous n’avez pas accès à cette section.</p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import PageContent from '@/components/PageContent.vue'
import aiService from '@/services/ai.service'
import MeetingStatusPill from './MeetingStatusPill.vue'
import {
  MEETING_ROLES,
  POLL_INTERVAL_MS,
  isProcessing,
  inputTypeLabel,
  statusLabel,
  normalizeText,
  formatMeetingDate,
  formatDateTime,
  formatFileSize,
  escapeHtml
} from './meetingHelpers'

/**
 * Onglet « Réunion CSE » du menu du domaine : liste des réunions déposées sur
 * le domaine actif (en-tête `Domain`), pour les élus (director, operator) et
 * les admins / community managers. Tant qu'une réunion est en traitement, la
 * liste se recharge toutes les 5 secondes.
 */
export default {
  name: 'app-meetings-list',
  components: { PageContent, MeetingStatusPill },
  data () {
    return {
      meetings: [],
      search: '',
      loading: true,
      loadError: '',
      deleting: null,
      pollTimer: null
    }
  },
  computed: {
    ...mapGetters(['roles', 'domain']),
    hasAccess () {
      return this.roles.some(role => MEETING_ROLES.includes(role))
    },
    displayedMeetings () {
      const search = normalizeText(this.search)
      if (!search) {
        return this.meetings
      }
      return this.meetings.filter(meeting => {
        const haystack = normalizeText([
          meeting.title,
          meeting.audio_file && meeting.audio_file.name,
          inputTypeLabel(meeting.input_type),
          statusLabel(meeting.status)
        ].filter(Boolean).join(' '))
        return haystack.includes(search)
      })
    },
    processingCount () {
      return this.meetings.filter(meeting => isProcessing(meeting.status)).length
    },
    reviewCount () {
      return this.meetings.filter(meeting => meeting.status === 'reviewing').length
    },
    countLabel () {
      const total = this.meetings.length
      const shown = this.displayedMeetings.length
      const noun = total > 1 ? 'réunions' : 'réunion'
      return shown === total ? `${total} ${noun}` : `${shown} sur ${total} ${noun}`
    }
  },
  watch: {
    'domain.uuid' () {
      this.search = ''
      this.loadMeetings()
    }
  },
  created () {
    this.loadMeetings()
  },
  beforeDestroy () {
    this.stopPolling()
  },
  methods: {
    formatMeetingDate,
    formatDateTime,
    formatFileSize,
    loadMeetings (silent = false) {
      this.stopPolling()
      if (!this.domain || !this.domain.uuid) {
        return
      }
      if (!silent) {
        this.loading = true
      }
      this.loadError = ''
      aiService.listMeetings().then(data => {
        this.meetings = Array.isArray(data) ? data : []
        this.schedulePolling()
      }).catch(err => {
        const status = err && err.response ? err.response.status : null
        this.loadError = status === 403
          ? 'Vous n’avez pas le rôle requis pour consulter les réunions CSE.'
          : 'Impossible de charger les réunions.'
        if (!silent) {
          this.meetings = []
        }
      }).finally(() => {
        this.loading = false
      })
    },
    schedulePolling () {
      this.stopPolling()
      if (this.processingCount) {
        this.pollTimer = window.setTimeout(() => this.loadMeetings(true), POLL_INTERVAL_MS)
      }
    },
    stopPolling () {
      if (this.pollTimer) {
        window.clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    },
    open (meeting) {
      this.$router.push({ name: 'app-meeting', params: { uuid: meeting.uuid } }).catch(() => {})
    },
    confirmDelete (meeting) {
      if (this.deleting) {
        return
      }
      const title = escapeHtml(meeting.title || 'cette réunion')
      this.$buefy.dialog.confirm({
        title: 'Supprimer la réunion',
        message: `Supprimer <b>${title}</b> ainsi que son mot à mot et ses livrables ? Cette action est définitive.`,
        type: 'is-danger',
        hasIcon: true,
        icon: 'exclamation-triangle',
        cancelText: 'Annuler',
        confirmText: 'Supprimer',
        onConfirm: () => this.deleteMeeting(meeting)
      })
    },
    deleteMeeting (meeting) {
      this.deleting = meeting.uuid
      aiService.deleteMeeting(meeting.uuid).then(() => {
        this.meetings = this.meetings.filter(item => item.uuid !== meeting.uuid)
        this.schedulePolling()
        this.notify('is-success', `<strong>${escapeHtml(meeting.title || 'La réunion')}</strong> a été supprimée.`)
      }).catch(err => {
        const status = err && err.response ? err.response.status : null
        this.notify('is-danger', status === 403
          ? 'Vous n’avez pas le rôle requis pour supprimer une réunion.'
          : 'Impossible de supprimer cette réunion.')
      }).finally(() => {
        this.deleting = null
      })
    },
    notify (type, message) {
      this.$buefy.notification.open({
        duration: 4000,
        message,
        position: 'is-bottom-right',
        type,
        hasIcon: false
      })
    }
  }
}
</script>

<style src="./meetings.css"></style>

<style scoped>
.meetings-table-scroll {
  contain: paint;
  flex: 1 1 auto;
  max-width: 100%;
  min-height: 0;
  min-width: 0;
  overflow: auto;
  width: 100%;
}

.meetings-table {
  width: 100%;
  min-width: 960px;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.meetings-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 12px 14px;
  background: #f9fafb;
  color: #4b5563;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.meetings-table td {
  box-sizing: border-box;
  height: 52px;
  padding: 6px 14px;
  border-top: 1px solid #e5e7eb;
  color: #111827;
  font-size: 14px;
  line-height: 1.25rem;
  vertical-align: middle;
}

.meetings-table th:first-child,
.meetings-table td:first-child {
  padding-left: 28px;
}

.meetings-table tbody tr {
  height: 52px;
}

.meetings-table tbody tr:hover {
  background: #f9fafb;
}

.meetings-row {
  cursor: pointer;
}

.meetings-row:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: -2px;
}

.meetings-column--date {
  width: 170px;
}

.meetings-column--type {
  width: 120px;
}

.meetings-column--status {
  width: 170px;
}

.meetings-column--created {
  width: 190px;
}

.meetings-column--delete {
  width: 85px;
}

.meetings-table th.meetings-column--delete {
  text-align: center;
}

.meetings-table td.meetings-column--delete {
  padding: 0;
  text-align: center;
}

.meetings-title {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meetings-detail {
  display: block;
  color: #6b7280;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meetings-detail--error {
  color: #b91c1c;
}

.meetings-type {
  display: inline-flex;
  padding: 5px 10px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 14px;
  font-weight: 600;
}

.meetings-type--audio {
  background: #dbeafe;
  color: #1d4ed8;
}

.meetings-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 52px;
  border: 0;
  background: transparent;
  color: #9ca3af;
  font-size: 17px;
  transition: color 0.15s ease;
}

.meetings-delete:hover:not(:disabled) {
  color: #ef4444;
}

.meetings-delete:disabled {
  opacity: 0.6;
}

.meetings-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
}

.meetings-empty p {
  max-width: 36rem;
  margin: 0;
}

.meetings-empty__title {
  color: #111827;
  font-weight: 600;
}

.meetings-footer__processing {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: #1d4ed8;
}

.meetings-footer__review {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: #92400e;
}

.meetings-footer__count {
  margin-left: auto;
}
</style>
